/**
 * APEX Verity — Cookie Consent State
 * ----------------------------------
 * Single source of truth for cookie consent decisions.
 *
 * Storage: localStorage key `apex-cookie-consent` (value: JSON).
 * Expiry: 12 months from the decision date — beyond that the user is
 *         re-prompted, matching ICO / EDPB guidance.
 *
 * Categories:
 *   essential   — always true, not user-toggleable
 *   analytics   — Google Analytics (gtag.js) gate
 *   preferences — UI prefs (theme, layout, dismissed banners, etc.)
 *
 * Public API
 *   STORAGE_KEY      — exported constant for direct access if needed
 *   loadConsent()    — read parsed object (or null if absent / expired / corrupt)
 *   saveConsent({analytics, preferences}) — write decision (essential always true)
 *   clearConsent()   — wipe (used by "withdraw consent" flow)
 *   hasResponded()   — has the user made a choice that is still valid?
 *   onConsentChange(fn) — subscribe; returns unsubscribe function
 *   emitConsentChange() — manually trigger subscribers (after saveConsent)
 *   maybeLoadAnalytics() — inject gtag.js + init iff analytics consent granted
 */

export const STORAGE_KEY = 'apex-cookie-consent';
export const CONSENT_VERSION = 1;
export const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

const GA_MEASUREMENT_ID = 'G-B71WPMJZ0D';

// ─────────────────────────── Storage layer ──────────────────────────────────

export function loadConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.expires_at !== 'number' || parsed.expires_at < Date.now()) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function saveConsent({ analytics, preferences }) {
  if (typeof window === 'undefined') return null;
  const now = Date.now();
  const record = {
    version: CONSENT_VERSION,
    essential: true,
    analytics: Boolean(analytics),
    preferences: Boolean(preferences),
    decided_at: now,
    expires_at: now + TWELVE_MONTHS_MS,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    emitConsentChange();
    return record;
  } catch (_) {
    return null;
  }
}

export function clearConsent() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    emitConsentChange();
  } catch (_) { /* no-op */ }
}

export function hasResponded() {
  return loadConsent() !== null;
}

// ─────────────────────────── Pub/sub ────────────────────────────────────────

const listeners = new Set();

export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function emitConsentChange() {
  const state = loadConsent();
  listeners.forEach((fn) => {
    try { fn(state); } catch (_) { /* listener errors must not break others */ }
  });
}

// ─────────────────────────── Google Analytics gate ──────────────────────────
// Single source of truth: GA only loads when the user has affirmatively
// granted analytics consent. Safe to call multiple times — idempotent.

let _gaInjected = false;

export function maybeLoadAnalytics() {
  if (typeof window === 'undefined') return;
  const c = loadConsent();
  if (!c || !c.analytics) return;
  if (_gaInjected) return;
  if (typeof window.gtag === 'function') { _gaInjected = true; return; }

  // Inject gtag.js
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  _gaInjected = true;
}
